import { useState, useCallback } from 'react';
import { trpc } from '../../lib/trpc';
import { MediaPickerModal } from './MediaPickerModal';
import type { Media } from '@wineryxplorer/shared';
import { Button } from '../ui/Button';
import { FormSection } from '../ui/FormSection';

export interface VenueMediaManagerProps {
  venueId: string;
  logoImageId?: string;
  heroImageIds: string[];
  galleryImageIds: string[];
  onLogoChange?: (logoId: string | null) => void;
  onHeroImagesChange?: (heroImageIds: string[]) => void;
  onGalleryImagesChange?: (galleryImageIds: string[]) => void;
  className?: string;
}

type PickerMode = 'logo' | 'hero' | 'gallery' | null;

export const VenueMediaManager = ({
  venueId,
  logoImageId,
  heroImageIds,
  galleryImageIds,
  onLogoChange,
  onHeroImagesChange,
  onGalleryImagesChange,
  className = '',
}: VenueMediaManagerProps) => {
  const [activePickerMode, setActivePickerMode] = useState<PickerMode>(null);
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>([]);
  const utils = trpc.useContext();

  // Fetch media details for display
  const { data: logoMedia } = trpc.media.getById.useQuery(
    { id: logoImageId || '' },
    { enabled: !!logoImageId }
  );

  const { data: heroMediaList } = trpc.media.getByIds.useQuery(
    { ids: heroImageIds },
    { enabled: heroImageIds.length > 0 }
  );

  const { data: galleryMediaList } = trpc.media.getByIds.useQuery(
    { ids: galleryImageIds },
    { enabled: galleryImageIds.length > 0 }
  );

  // Mutations for updating venue media
  const updateVenueMutation = trpc.venue.update.useMutation({
    onSuccess: () => {
      utils.venue.getById.invalidate({ id: venueId });
    },
    onError: () => {
      alert('Failed to save changes. Please try again.');
    },
  });

  const updateHeroImagesMutation = trpc.venue.updateHeroImages.useMutation({
    onSuccess: () => {
      utils.venue.getById.invalidate({ id: venueId });
    },
    onError: () => {
      alert('Failed to save hero images. Please try again.');
    },
  });

  // Handle temporary selection changes (while modal is open)
  const handleTempSelectionChange = useCallback((selectedIds: string[]) => {
    setTempSelectedIds(selectedIds);
  }, []);

  // Handle logo selection confirmation (when Save is clicked)
  const handleLogoSelection = useCallback(() => {
    const logoId = tempSelectedIds.length > 0 ? tempSelectedIds[0] : null;
    
    // Always update venue directly for immediate persistence
    updateVenueMutation.mutate({
      id: venueId,
      logoImageId: logoId || undefined,
    });
    
    // Also call callback if provided (for form state synchronization)
    if (onLogoChange) {
      onLogoChange(logoId);
    }
    
    setActivePickerMode(null);
    setTempSelectedIds([]);
  }, [tempSelectedIds, onLogoChange, updateVenueMutation, venueId]);

  // Handle hero images selection confirmation (when Save is clicked)
  const handleHeroImagesSelection = useCallback(() => {
    // Always update venue directly for immediate persistence
    updateHeroImagesMutation.mutate({
      venueId,
      heroImageIds: tempSelectedIds,
    });
    
    // Also call callback if provided (for form state synchronization)
    if (onHeroImagesChange) {
      onHeroImagesChange(tempSelectedIds);
    }
    
    setActivePickerMode(null);
    setTempSelectedIds([]);
  }, [tempSelectedIds, onHeroImagesChange, updateHeroImagesMutation, venueId]);

  // Handle gallery images selection confirmation (when Save is clicked)
  const handleGalleryImagesSelection = useCallback(() => {
    // Always update venue directly for immediate persistence
    updateVenueMutation.mutate({
      id: venueId,
      imageIds: tempSelectedIds,
    });
    
    // Also call callback if provided (for form state synchronization)
    if (onGalleryImagesChange) {
      onGalleryImagesChange(tempSelectedIds);
    }
    
    setActivePickerMode(null);
    setTempSelectedIds([]);
  }, [tempSelectedIds, onGalleryImagesChange, updateVenueMutation, venueId]);

  // Handle picker close
  const handlePickerClose = useCallback(() => {
    setActivePickerMode(null);
    setTempSelectedIds([]);
  }, []);

  // Initialize temp selection when modal opens
  const handlePickerOpen = useCallback((mode: PickerMode) => {
    setActivePickerMode(mode);
    // Initialize temp selection with current values
    switch (mode) {
      case 'logo':
        setTempSelectedIds(logoImageId ? [logoImageId] : []);
        break;
      case 'hero':
        setTempSelectedIds(heroImageIds);
        break;
      case 'gallery':
        setTempSelectedIds(galleryImageIds);
        break;
    }
  }, [logoImageId, heroImageIds, galleryImageIds]);

  // Cast media lists to proper types
  const typedLogoMedia = logoMedia as Media | undefined;
  const typedHeroMediaList = (heroMediaList ?? []) as Media[];
  const typedGalleryMediaList = (galleryMediaList ?? []) as Media[];

  // Render media preview
  const renderMediaPreview = (media: Media | undefined, alt: string) => {
    if (!media) {
      return (
        <div className="w-24 h-24 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      );
    }

    return (
      <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
        <img
          src={media.cloudinaryUrl}
          alt={alt}
          className="w-full h-full object-cover"
        />
      </div>
    );
  };

  // Render media grid for multiple images
  const renderMediaGrid = (mediaList: Media[] | undefined, emptyText: string) => {
    if (!mediaList || mediaList.length === 0) {
      return (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 text-sm">{emptyText}</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {mediaList.map((media, index) => (
          <div key={media.id} className="relative group">
            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
              <img
                src={media.cloudinaryUrl}
                alt={media.filename}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Order indicator for hero images */}
            {activePickerMode === 'hero' && (
              <div className="absolute -top-2 -left-2 w-6 h-6 bg-primary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {index + 1}
              </div>
            )}
            
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-900 truncate">
                {media.filename}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={className}>
      {/* Logo Section */}
      <FormSection
        title="Logo"
        titleAs="h3"
        description="Select a single image to represent your venue."
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={() => handlePickerOpen('logo')}
          >
            {logoImageId ? 'Change Logo' : 'Select Logo'}
          </Button>
        }
      >
        
        <div className="flex items-center space-x-4">
          {renderMediaPreview(typedLogoMedia, 'Venue logo')}
          
          {logoImageId && (
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                {typedLogoMedia?.filename || 'Loading...'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Currently selected as venue logo
              </p>
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  setTempSelectedIds([]);
                  handleLogoSelection();
                }}
                className="text-red-600 hover:text-red-700"
              >
                Remove logo
              </Button>
            </div>
          )}
        </div>
      </FormSection>

      {/* Hero Images Section */}
      <FormSection
        title="Hero Images"
        titleAs="h3"
        description="Select up to 5 images for the hero carousel (drag to reorder)."
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={() => handlePickerOpen('hero')}
          >
            {heroImageIds.length > 0 ? 'Manage Hero Images' : 'Select Hero Images'}
          </Button>
        }
      >
        
        {renderMediaGrid(typedHeroMediaList, 'No hero images selected')}
        
        {heroImageIds.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>{heroImageIds.length} of 5 hero images selected</span>
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setTempSelectedIds([]);
                handleHeroImagesSelection();
              }}
              className="text-red-600 hover:text-red-700"
            >
              Clear all
            </Button>
          </div>
        )}
      </FormSection>

      {/* Gallery Images Section */}
      <FormSection
        title="Gallery Images"
        titleAs="h3"
        description="Select multiple images for the venue gallery."
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={() => handlePickerOpen('gallery')}
          >
            {galleryImageIds.length > 0 ? 'Manage Gallery Images' : 'Select Gallery Images'}
          </Button>
        }
      >
        
        {renderMediaGrid(typedGalleryMediaList, 'No gallery images selected')}
        
        {galleryImageIds.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>{galleryImageIds.length} gallery images selected</span>
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setTempSelectedIds([]);
                handleGalleryImagesSelection();
              }}
              className="text-red-600 hover:text-red-700"
            >
              Clear all
            </Button>
          </div>
        )}
      </FormSection>

      {/* Media Picker Modals */}
      <MediaPickerModal
        isOpen={activePickerMode === 'logo'}
        onClose={handlePickerClose}
        mode="single"
        venueId={venueId}
        selectedIds={tempSelectedIds}
        onSelectionChange={handleTempSelectionChange}
        onSave={handleLogoSelection}
        title="Select Venue Logo"
        maxSelection={1}
        isSaving={updateVenueMutation.isPending}
      />

      <MediaPickerModal
        isOpen={activePickerMode === 'hero'}
        onClose={handlePickerClose}
        mode="multi-ordered"
        venueId={venueId}
        selectedIds={tempSelectedIds}
        onSelectionChange={handleTempSelectionChange}
        onSave={handleHeroImagesSelection}
        title="Select Hero Images"
        maxSelection={5}
        isSaving={updateHeroImagesMutation.isPending}
      />

      <MediaPickerModal
        isOpen={activePickerMode === 'gallery'}
        onClose={handlePickerClose}
        mode="multi"
        venueId={venueId}
        selectedIds={tempSelectedIds}
        onSelectionChange={handleTempSelectionChange}
        onSave={handleGalleryImagesSelection}
        title="Select Gallery Images"
        maxSelection={50}
        isSaving={updateVenueMutation.isPending}
      />
    </div>
  );
};